"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getSortFields = exports.flipSort = exports.flipDirection = exports.sortRecords = exports.naturalSort = void 0;
const fast_sort_1 = require("fast-sort");
const fields_logic_registry_1 = require("../field-type-registry/fields-logic-registry");
const fields_1 = require("../utils/fields");
exports.naturalSort = (0, fast_sort_1.createNewSortInstance)({
    comparer: (a, b, order) => {
        if (typeof a === "string" && typeof b === "string") {
            return naturalCompare(a.toLowerCase(), b.toLowerCase(), order);
        }
        return defaultComparer(a, b, order);
    },
});
/**
 * Search through records with text query in selected fields.
 * Each field value is converted to the string and checked
 * for match with the query
 */
function sortRecords(records = [], fields, sortRules = [], userPreferences, groupByRules = [], reportId) {
    const rules = [
        ...groupByRules.map((rule) => ({ ...rule, isGroupByRule: true })),
        ...sortRules.map((rule) => ({ ...rule, isGroupByRule: false })),
    ];
    if (!rules.length && !reportId) {
        return records;
    }
    const fieldsMap = (0, fields_1.fieldsToMap)(fields);
    const valueGetters = rules.map((rule) => {
        var _a;
        const field = fieldsMap.get(rule.field);
        const parentField = (_a = field === null || field === void 0 ? void 0 : field.parent) !== null && _a !== void 0 ? _a : field;
        if (!parentField || !field) {
            return () => ({ value: undefined });
        }
        const fieldLogic = (0, fields_logic_registry_1.getFieldLogic)(parentField.field_type);
        return (record) => {
            const fieldValue = (0, fields_1.getValueFromRecordByField)(record, parentField);
            const sortKey = getKey(fieldValue, parentField, fieldLogic, record, fields, rule, userPreferences);
            const sortItem = {
                value: sortKey,
                useNaturalSorting: fieldLogic.useNaturalSorting(parentField, fieldValue, rule.isGroupByRule),
            };
            return sortItem;
        };
    });
    if (reportId) {
        valueGetters.push((record) => {
            const ranking = record.ranking;
            if (ranking) {
                return {
                    value: ranking[reportId] || ranking.default,
                    useNaturalSorting: false,
                };
            }
            return {
                value: "aaaaaaaaaa",
                useNaturalSorting: false,
            };
        });
    }
    return sortBy(records, valueGetters, rules);
}
exports.sortRecords = sortRecords;
function sortBy(records, valueGetters, rules) {
    const alphaNumCache = new Map();
    const decorate = records.map((record) => ({
        keys: valueGetters.map((getter) => getter(record)),
        record,
    }));
    decorate.sort((a, b) => {
        var _a;
        for (let i = 0; i < a.keys.length; i++) {
            const keyA = a.keys[i];
            const keyB = b.keys[i];
            if (keyA.value === keyB.value)
                continue;
            const order = universalCompare(keyA, keyB, 1, alphaNumCache);
            if (order !== 0) {
                const direction = ((_a = rules[i]) === null || _a === void 0 ? void 0 : _a.direction) === "desc" ? -1 : 1;
                return order * direction;
            }
        }
        return 0;
    });
    return decorate.map((item) => item.record);
}
function getKey(fieldValue, currentField, fieldLogic, record, fields, rule, userPreferences) {
    if (rule.isGroupByRule) {
        return fieldLogic.groupBySortKey(fieldValue, currentField, userPreferences, record, fields, rule);
    }
    return fieldLogic.sortKey(fieldValue, currentField, userPreferences, record, fields, rule);
}
function universalCompare(a, b, order, alphaNumCache) {
    const { value: valueA } = a;
    const { value: valueB } = b;
    if (a.useNaturalSorting &&
        typeof valueA === "string" &&
        typeof valueB === "string") {
        return naturalCompare(valueA, valueB, order, alphaNumCache);
    }
    if (isSortItemArray(a) && isSortItemArray(b)) {
        return compareArrays(a.value, b.value, order, a.useNaturalSorting, alphaNumCache);
    }
    return defaultComparer(valueA, valueB, order);
}
function isSortItemArray(item) {
    return Array.isArray(item.value);
}
function naturalCompare(a, b, order, alphaNumCache) {
    return compareArrays(splitAlphaNum(a, alphaNumCache), splitAlphaNum(b, alphaNumCache), order);
}
function compareArrays(a, b, order, useNaturalSorting, alphaNumCache) {
    const len = Math.max(a.length, b.length);
    for (let i = 0; i < len; i++) {
        const itemA = a[i];
        const itemB = b[i];
        if (itemA === itemB)
            continue;
        if (itemA === undefined)
            return -1;
        if (itemB === undefined)
            return 1;
        let res = 0;
        if (useNaturalSorting &&
            typeof itemA === "string" &&
            typeof itemB === "string") {
            res = naturalCompare(itemA, itemB, order, alphaNumCache);
            if (res !== 0)
                return res;
        }
        if (Array.isArray(itemA) && Array.isArray(itemB)) {
            const res = compareArrays(itemA, itemB, order, useNaturalSorting, alphaNumCache);
            if (res !== 0)
                return res;
        }
        res = defaultComparer(itemA, itemB, -1);
        if (res !== 0)
            return res;
    }
    return 0;
}
const splitAlphaNum = (str, alphaNumCache) => {
    var _a;
    if (!(alphaNumCache === null || alphaNumCache === void 0 ? void 0 : alphaNumCache.has(str))) {
        const result = str
            .split(/(\d+)/)
            .map((part, i) => (i % 2 === 1 ? +part : part));
        alphaNumCache === null || alphaNumCache === void 0 ? void 0 : alphaNumCache.set(str, result);
        return result;
    }
    return (_a = alphaNumCache.get(str)) !== null && _a !== void 0 ? _a : [];
};
function defaultComparer(a, b, order) {
    if (a === null || a === undefined) {
        return order;
    }
    if (b === null || b === undefined) {
        return -order;
    }
    if (typeof a !== typeof b) {
        return typeof a < typeof b ? -1 : 1;
    }
    if (typeof a === "string" && typeof b === "string") {
        return a.localeCompare(b);
    }
    if (a < b) {
        return -1;
    }
    if (a > b) {
        return 1;
    }
    return 0;
}
/**
 * Flip sort direction to the opposite.
 */
function flipDirection(direction) {
    return direction === "asc" ? "desc" : "asc";
}
exports.flipDirection = flipDirection;
/**
 * Flip sort direction to the opposite, keeping the same field.
 */
function flipSort(sort) {
    return { ...sort, direction: flipDirection(sort.direction) };
}
exports.flipSort = flipSort;
function getSortFields(rules) {
    return rules.map((rule) => rule.field);
}
exports.getSortFields = getSortFields;
